/**
 * PHP 镜像表 (raw) 浏览器
 *
 * 列出 `php_*` 表（来自 `pnpm --filter @uo/backend php:mirror-all`），
 * 选中后展示分页行。后端：`/api/admin/php-mirror/*`，RBAC：`system.php_mirror`。
 *
 * 这是只读视图 — 用来核对从旧 PHP 后台导入的原始数据。
 */
import { useMemo, useState } from 'react';
import {
  Card, Col, Drawer, Empty, Input, Row, Select, Space, Statistic, Table, Tag, Typography,
} from 'antd';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';

interface MirrorTable {
  table_name: string;
  php_href: string;
  label_zh: string | null;
  server_total: number | null;
  row_count: number;
  column_count: number;
  last_synced_at: string;
}

interface RowsResponse {
  table: string;
  total: number;
  page: number;
  page_size: number;
  columns: string[];
  rows: Record<string, unknown>[];
}

interface RowDetail {
  table: string;
  id: string;
  _raw: unknown;
  row: Record<string, unknown>;
}

function fmtCell(v: unknown): string {
  if (v === null || v === undefined || v === '') return '';
  if (typeof v === 'object') {
    try { return JSON.stringify(v); } catch { return String(v); }
  }
  const s = String(v);
  return s.length > 200 ? `${s.slice(0, 200)}…` : s;
}

export function PhpMirrorPage() {
  const [selected, setSelected] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<'id_desc' | 'id_asc' | 'imported_desc' | 'imported_asc'>('id_desc');
  const [drawerRow, setDrawerRow] = useState<RowDetail | null>(null);

  const tablesQ = useQuery<{ tables: MirrorTable[] }>({
    queryKey: ['php-mirror', 'tables'],
    queryFn: () => api.get<{ tables: MirrorTable[] }>('/api/admin/php-mirror/tables'),
    staleTime: 15_000,
  });

  const rowsQ = useQuery<RowsResponse>({
    queryKey: ['php-mirror', 'rows', selected, page, pageSize, q, sort],
    queryFn: () =>
      api.get<RowsResponse>(`/api/admin/php-mirror/${encodeURIComponent(selected ?? '')}/rows`, {
        page, page_size: pageSize, q: q || undefined, sort,
      }),
    enabled: !!selected,
    staleTime: 0,
  });

  const tableOptions = useMemo(
    () =>
      (tablesQ.data?.tables ?? []).map((t) => ({
        label: `${t.label_zh ?? t.table_name}  ·  ${t.row_count}/${t.server_total ?? '?'} 行 · ${t.column_count} 列`,
        value: t.table_name,
      })),
    [tablesQ.data],
  );

  const meta = (tablesQ.data?.tables ?? []).find((t) => t.table_name === selected) ?? null;

  const columns = (rowsQ.data?.columns ?? [])
    .filter((c) => c !== '_imported_at')
    .map((c) => ({
      title: c,
      dataIndex: c,
      key: c,
      width: c === 'id' ? 90 : undefined,
      ellipsis: true,
      render: (v: unknown) => fmtCell(v),
    }));

  const totalsRow = (
    <Row gutter={12}>
      <Col span={6}>
        <Statistic title="镜像表数" value={tablesQ.data?.tables?.length ?? 0} />
      </Col>
      <Col span={6}>
        <Statistic
          title="总行数（已镜像）"
          value={(tablesQ.data?.tables ?? []).reduce((s, t) => s + Number(t.row_count || 0), 0)}
        />
      </Col>
      <Col span={6}>
        <Statistic
          title="选中表行数"
          value={meta?.row_count ?? 0}
          suffix={meta?.server_total ? `/ ${meta.server_total}` : ''}
        />
      </Col>
      <Col span={6}>
        <Statistic
          title="选中表列数"
          value={meta?.column_count ?? 0}
        />
      </Col>
    </Row>
  );

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card size="small" title="PHP 旧后台 → Postgres 镜像表" bordered>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
          数据来自 <Typography.Text code>scripts/php-mirror-all.ts</Typography.Text>。
          每张旧 PHP 表自动建为 <Typography.Text code>php_&lt;slug&gt;</Typography.Text>，
          列结构按行键自适应（新键自动 ALTER TABLE ADD COLUMN）。
          原始 JSON 同时存储在 <Typography.Text code>_raw</Typography.Text> 字段。
        </Typography.Paragraph>
        {totalsRow}
      </Card>

      <Card
        size="small"
        title="选择镜像表"
        extra={
          <Space>
            <a onClick={() => tablesQ.refetch()}>
              <ReloadOutlined /> 刷新表列表
            </a>
          </Space>
        }
      >
        {tablesQ.isLoading ? (
          '加载中…'
        ) : (tablesQ.data?.tables?.length ?? 0) === 0 ? (
          <Empty
            description={
              <span>
                还没有镜像表。在仓库根目录执行：
                <Typography.Text code>
                  UO_CURL_JSON=$(pwd)/curl.json pnpm --filter @uo/backend php:mirror-all
                </Typography.Text>
              </span>
            }
          />
        ) : (
          <Select
            value={selected ?? undefined}
            onChange={(v) => { setSelected(v); setPage(1); setQ(''); }}
            placeholder="选择要查看的 PHP 镜像表…"
            style={{ width: '100%', maxWidth: 720 }}
            showSearch
            optionFilterProp="label"
            options={tableOptions}
          />
        )}
        {meta && (
          <div style={{ marginTop: 12 }}>
            <Tag>{meta.table_name}</Tag>
            <Tag color="blue">{meta.php_href}</Tag>
            <Tag>列 {meta.column_count}</Tag>
            <Tag>同步 {new Date(meta.last_synced_at).toLocaleString('zh-CN')}</Tag>
          </div>
        )}
      </Card>

      {selected && (
        <Card size="small" title={`数据 — ${selected}`}>
          <Space style={{ marginBottom: 12 }} wrap>
            <Input
              prefix={<SearchOutlined />}
              placeholder="按 _raw 内容搜索 (ILIKE)…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onPressEnter={() => { setPage(1); rowsQ.refetch(); }}
              allowClear
              style={{ width: 280 }}
            />
            <Select
              value={sort}
              onChange={(v) => { setSort(v); setPage(1); }}
              style={{ width: 180 }}
              options={[
                { label: 'id 降序', value: 'id_desc' },
                { label: 'id 升序', value: 'id_asc' },
                { label: '导入时间↓', value: 'imported_desc' },
                { label: '导入时间↑', value: 'imported_asc' },
              ]}
            />
            <a onClick={() => rowsQ.refetch()}>
              <ReloadOutlined /> 重载
            </a>
          </Space>
          <Table
            size="small"
            loading={rowsQ.isFetching}
            scroll={{ x: 'max-content' }}
            dataSource={rowsQ.data?.rows ?? []}
            rowKey={(r) => String((r as { id?: unknown }).id ?? Math.random())}
            columns={columns}
            onRow={(record) => ({
              onClick: async () => {
                if (!selected) return;
                const id = String((record as { id?: unknown }).id ?? '');
                if (!id) return;
                const detail = await api.get<RowDetail>(
                  `/api/admin/php-mirror/${encodeURIComponent(selected)}/row/${encodeURIComponent(id)}`,
                );
                setDrawerRow(detail);
              },
              style: { cursor: 'pointer' },
            })}
            pagination={{
              current: page,
              pageSize,
              total: rowsQ.data?.total ?? 0,
              showSizeChanger: true,
              pageSizeOptions: ['20', '50', '100', '200'],
              onChange: (p, ps) => { setPage(p); setPageSize(ps); },
              showTotal: (t) => `共 ${t} 行`,
            }}
          />
        </Card>
      )}

      <Drawer
        open={!!drawerRow}
        onClose={() => setDrawerRow(null)}
        title={drawerRow ? `${drawerRow.table} · id=${drawerRow.id}` : '行详情'}
        width={720}
      >
        {drawerRow && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Card size="small" title="字段（来自镜像列）">
              <Table
                size="small"
                showHeader={false}
                pagination={false}
                dataSource={Object.entries(drawerRow.row).map(([k, v]) => ({ key: k, k, v: fmtCell(v) }))}
                columns={[
                  { dataIndex: 'k', width: 200 },
                  { dataIndex: 'v', ellipsis: false },
                ]}
              />
            </Card>
            <Card size="small" title="原始 JSON (_raw)">
              <pre style={{ whiteSpace: 'pre-wrap', maxHeight: 360, overflow: 'auto' }}>
                {JSON.stringify(drawerRow._raw, null, 2)}
              </pre>
            </Card>
          </Space>
        )}
      </Drawer>
    </Space>
  );
}
